import { MathUtils, PerspectiveCamera, Vector3 } from 'three';

export type CameraMode = 'overview' | 'follow' | 'decision' | 'throw' | 'result';

const PRESETS: Record<CameraMode, { position: Vector3; look: Vector3 }> = {
  overview: { position: new Vector3(-34, 28, 38), look: new Vector3(-10, 1, 0) },
  follow: { position: new Vector3(-30, 10, 27), look: new Vector3(-20, 4, 0) },
  decision: { position: new Vector3(13, 11.5, 25), look: new Vector3(-5, 1.4, 0) },
  throw: { position: new Vector3(12.5, 6.6, 17.5), look: new Vector3(-4, 1, 0) },
  result: { position: new Vector3(25, 24, 40), look: new Vector3(10, 1, 0) },
};

export class CameraController {
  readonly camera: PerspectiveCamera;
  private targetPosition = PRESETS.overview.position.clone();
  private targetLook = PRESETS.overview.look.clone();
  private currentLook = PRESETS.overview.look.clone();
  private shake = 0;
  private shakeEnabled = true;

  constructor(aspect: number) {
    this.camera = new PerspectiveCamera(48, aspect, 0.1, 260);
    this.camera.position.copy(PRESETS.overview.position);
    this.camera.lookAt(PRESETS.overview.look);
  }

  setMode(mode: CameraMode, immediate = false): void {
    this.targetPosition.copy(PRESETS[mode].position);
    this.targetLook.copy(PRESETS[mode].look);
    if (immediate) {
      this.camera.position.copy(this.targetPosition);
      this.currentLook.copy(this.targetLook);
      this.camera.lookAt(this.currentLook);
    }
  }

  focus(position: Vector3): void {
    this.targetLook.copy(position);
  }

  setShake(enabled: boolean): void {
    this.shakeEnabled = enabled;
  }

  bump(amount: number): void {
    if (this.shakeEnabled) this.shake = Math.max(this.shake, amount);
  }

  update(delta: number, reducedMotion: boolean): void {
    const speed = reducedMotion ? 14 : 3.4;
    const alpha = 1 - Math.exp(-delta * speed);
    this.camera.position.lerp(this.targetPosition, alpha);
    this.currentLook.lerp(this.targetLook, alpha);
    if (this.shake > 0.002 && !reducedMotion) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake;
      this.camera.position.y += (Math.random() - 0.5) * this.shake;
      this.shake *= Math.pow(0.12, delta);
    }
    this.camera.lookAt(this.currentLook);
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.fov = MathUtils.clamp(48 + Math.max(0, 0.8 - width / height) * 12, 46, 60);
    this.camera.updateProjectionMatrix();
  }
}
